import {
  Dialog,
  EmptyState,
  IconButton,
  ListX,
  LoadingIndicator,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { Box, Stack, Typography } from "@mui/material";
import { GetInvestigationSummaryRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getInvestigationSummaryRPC";
import { Copy } from "lucide-react";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { useGetInvestigationSummaryStore } from "../../stores/Hooks";
import { EMPTY_CHARACTER, useBorder } from "@pulse/shared-components";

const INVESTIGATION_SUMMARY_DIALOG_WIDTH = "960px";

interface InvestigationSummaryDialogProps {
  emailId: string;
  onClose: VoidFunction;
}

type DialogToOpen = "INVESTIGATION_SUMMARY" | "UNEXPECTED_ERROR";

export const InvestigationSummaryDialog: React.FC<InvestigationSummaryDialogProps> =
  observer(({ emailId, onClose }) => {
    const [dialogToOpen, setDialogToOpen] = useState<DialogToOpen>(
      "INVESTIGATION_SUMMARY",
    );

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const borderRadius = useCornerRadius();
    const border = useBorder();
    const navigateTo = useRouteNavigator();
    const investigationSummaryStore = useGetInvestigationSummaryStore();

    useEffect(() => {
      investigationSummaryStore
        .getInvestigationSummary(emailId)
        .catch(() => setDialogToOpen("UNEXPECTED_ERROR"));

      return investigationSummaryStore.resetStore;
    }, [emailId, investigationSummaryStore]);

    useEffect(() => {
      if (!investigationSummaryStore.getInvestigationSummaryRPCError) return;

      switch (investigationSummaryStore.getInvestigationSummaryRPCError) {
        case GetInvestigationSummaryRPC.RPCError.InvalidEmailId:
          navigateTo.pageNotFound();
          break;
        case GetInvestigationSummaryRPC.RPCError
          .InvestigationSummaryNotAvailable:
          // We will show empty state in "INVESTIGATION_SUMMARY" dialog.
          break;
        default:
          investigationSummaryStore.getInvestigationSummaryRPCError satisfies never;
      }
    }, [
      investigationSummaryStore,
      investigationSummaryStore.getInvestigationSummaryRPCError,
      navigateTo,
    ]);

    return (
      <>
        <Dialog
          open={dialogToOpen === "INVESTIGATION_SUMMARY"}
          title={t("emailCycles.investigationSummary")}
          onClose={onClose}
          showCloseButton
          width={INVESTIGATION_SUMMARY_DIALOG_WIDTH}
          contentPadding={spacing.spaceLG}
          disableBackdropClick={true}
        >
          <Stack width="100%">
            {investigationSummaryStore.loadingInvestigationSummary ? (
              <LoadingIndicator
                variant="container"
                loadingText={t("common.detailsLoadingState")}
                isLoading
              />
            ) : investigationSummaryStore.summary ? (
              <Stack height="100%" width="100%" gap={spacing.spaceLG}>
                <Typography color={tokens.label} {...typography.sh2}>
                  {t("emailCycles.investigationSummaryEmailId", { emailId })}
                </Typography>
                <Stack height="100%" gap={spacing.spaceMD} overflow="auto">
                  <Stack
                    border={border.default}
                    borderRadius={borderRadius.radiusXS}
                    overflow="hidden"
                    color={tokens.label}
                    bgcolor={tokens.backgroundSubtle}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      padding={`${spacing.spaceSM} ${spacing.spaceLG}`}
                      borderBottom={border.default}
                    >
                      <Typography color={tokens.label} {...typography.s2}>
                        {t("emailCycles.summary")}
                      </Typography>
                      <IconButton
                        name="copyInvestigationSummaryButton"
                        size="small"
                        icon={<Copy />}
                        variant="plain-neutral"
                        hoverColor="buttonOnSurfaceIcon"
                        onClick={(): void => {
                          void navigator.clipboard.writeText(
                            investigationSummaryStore.summary,
                          );
                        }}
                      />
                    </Stack>
                    <Box
                      component="pre"
                      margin={0}
                      padding={spacing.spaceLG}
                      color={tokens.label}
                      overflow="auto"
                    >
                      {investigationSummaryStore.summary}
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            ) : (
              <EmptyState
                title={t(
                  "emailCycles.investigationSummaryNotAvailableErrorTitle",
                )}
                description={EMPTY_CHARACTER}
                width="100%"
                icon={<ListX />}
              />
            )}
          </Stack>
        </Dialog>

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t(
              "emailCycles.getInvestigationSummaryUnexpectedErrorMessage",
            )}
            onClickDone={onClose}
          />
        )}
      </>
    );
  });
